import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import moment from "moment-timezone";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Alert from "@material-ui/lab/Alert";
import { AgGridReact } from "ag-grid-react";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";

import { fetchWcOrders } from "../../../redux/features/sales/payoutsSlice";
import WcPayoutPreviewDialog from "./WcPayoutPreviewDialog";
import { wcOrderAdminUrl } from "./wcLinks";

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  propertySelect: {
    minWidth: 320,
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  selectionSummary: {
    padding: theme.spacing(2, 3),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[700]
        : theme.palette.primary.main,
    color: theme.palette.type === "dark" ? "inherit" : "#fff",
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  selectionSummaryText: {
    color:
      theme.palette.type === "dark"
        ? theme.palette.text.secondary
        : "rgba(255,255,255,0.8)",
  },
  loadingOverlay: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    height: 200,
  },
  skippedAlert: {
    marginTop: theme.spacing(2),
  },
}));

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const moneyFormatter = (params) =>
  params.value !== null && params.value !== undefined
    ? currencyFormatter.format(Number(params.value))
    : "";

const dateOnlyFormatter = (params) =>
  params.value ? moment(params.value, "YYYY-MM-DD").format("ll") : "";

// Sums in cents so a long selection doesn't drift by a penny.
const sumMoney = (rows, field) =>
  rows.reduce((total, row) => total + Math.round(Number(row[field]) * 100), 0) /
  100;

const WcPayoutNewPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const { mode } = useSelector((state) => state.ui);
  const {
    wcOrders,
    wcProperties,
    wcSkippedOrders,
    wcCommissionPct,
    isLoadingWcOrders,
    errorWcOrders,
  } = useSelector((state) => state.payouts);

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);

  const gridApiRef = useRef(null);

  useEffect(() => {
    dispatch(fetchWcOrders());
  }, [dispatch]);

  const selectedProperty = useMemo(
    () => wcProperties.find((p) => p.account_id === selectedAccountId) || null,
    [wcProperties, selectedAccountId]
  );

  // Unpaid order count and transfer total per property, for the picker.
  const propertyTotals = useMemo(() => {
    const totals = {};
    (wcOrders || []).forEach((o) => {
      const t = totals[o.account_id] || { count: 0, transfer: 0 };
      t.count += 1;
      t.transfer += Math.round(Number(o.transfer_amount) * 100);
      totals[o.account_id] = t;
    });
    return totals;
  }, [wcOrders]);

  const rowData = useMemo(
    () =>
      selectedAccountId
        ? (wcOrders || []).filter((o) => o.account_id === selectedAccountId)
        : [],
    [wcOrders, selectedAccountId]
  );
  const rowCount = rowData.length;
  const errorMessage = errorWcOrders?.errorMessage || "";

  const OrderIdCellRenderer = useCallback((params) => {
    const id = params.value;
    if (!id) return null;
    return (
      <a
        href={wcOrderAdminUrl(id)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", fontWeight: "bold" }}
      >
        {id}
      </a>
    );
  }, []);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        width: 50,
        minWidth: 50,
        pinned: "left",
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        headerName: "Order ID",
        field: "wc_order_id",
        filter: "agNumberColumnFilter",
        width: 110,
        pinned: "left",
        cellRendererFramework: OrderIdCellRenderer,
      },
      {
        headerName: "Date Placed",
        field: "date_order_placed",
        pinned: "left",
        minWidth: 160,
        valueFormatter: (params) =>
          params.value
            ? moment(params.value).tz("America/Chicago").format("lll")
            : "",
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Customer",
        field: "customer_name",
        filter: "agTextColumnFilter",
        minWidth: 150,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Reservation Name",
        field: "reservation_name",
        filter: "agTextColumnFilter",
        minWidth: 160,
      },
      {
        headerName: "Reservation #",
        field: "reservation_number",
        filter: "agTextColumnFilter",
        minWidth: 140,
      },
      {
        headerName: "Check In",
        field: "check_in",
        minWidth: 120,
        valueFormatter: dateOnlyFormatter,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Check Out",
        field: "check_out",
        minWidth: 120,
        valueFormatter: dateOnlyFormatter,
        cellStyle: { whiteSpace: "nowrap" },
      },
      {
        headerName: "Product",
        field: "product_name",
        filter: "agTextColumnFilter",
        minWidth: 220,
      },
      {
        headerName: "Qty",
        field: "quantity",
        type: "rightAligned",
        width: 80,
        minWidth: 80,
      },
      {
        headerName: "Status",
        field: "wc_status",
        filter: "agTextColumnFilter",
        minWidth: 120,
        valueFormatter: (params) =>
          params.value ? params.value.toUpperCase() : "",
      },
      {
        headerName: "Order Total",
        field: "total",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
      },
      {
        headerName: "Stripe Fee",
        field: "stripe_fee",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
      },
      {
        headerName: "Commission",
        field: "commission",
        type: "rightAligned",
        valueFormatter: moneyFormatter,
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Transfer Amount",
        field: "transfer_amount",
        type: "rightAligned",
        pinned: "right",
        width: 160,
        valueFormatter: moneyFormatter,
        cellStyle: { color: "#19a86d", fontWeight: "bold" },
      },
    ],
    [OrderIdCellRenderer]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 110,
    }),
    []
  );

  const onGridReady = useCallback(
    (params) => {
      gridApiRef.current = params.api;
      if (!rowCount || errorWcOrders) params.api.showNoRowsOverlay();
    },
    [errorWcOrders, rowCount]
  );

  const onSelectionChanged = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    setSelectedRows(api.getSelectedRows());
  }, []);

  const previewSummary = useMemo(() => {
    if (!selectedProperty || selectedRows.length === 0) return null;
    const orders = [...selectedRows].sort(
      (a, b) => a.wc_order_id - b.wc_order_id
    );
    return {
      property: selectedProperty,
      commissionPct: Number(wcCommissionPct) || 0,
      orders,
      totalOrdersAmount: sumMoney(orders, "total"),
      totalStripeFees: sumMoney(orders, "stripe_fee"),
      totalNet: sumMoney(orders, "stripe_net"),
      totalCommission: sumMoney(orders, "commission"),
      totalTransfer: sumMoney(orders, "transfer_amount"),
    };
  }, [selectedProperty, selectedRows, wcCommissionPct]);

  const handlePayoutSuccess = useCallback(
    (payoutData) => {
      setPreviewOpen(false);
      if (payoutData.dry_run) {
        setDryRunResult(payoutData);
        return;
      }
      history.push(`/sales/woocommerce-payouts/${payoutData.payout_id}`, {
        showSuccess: true,
        snackbarContent: `Payout #${
          payoutData.payout_id
        } created successfully for ${currencyFormatter.format(
          payoutData.total_transfer
        )}`,
      });
    },
    [history]
  );

  const handleRefresh = () => {
    setSelectedRows([]);
    setDryRunResult(null);
    dispatch(fetchWcOrders());
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            className={classes.backButton}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/sales/woocommerce-payouts")}
          >
            Back to WooCommerce Payouts
          </Button>
          <Typography variant="h6">New WooCommerce Payout</Typography>
        </Grid>

        {/* Step 1: Select Property */}
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <div className={classes.stepHeader}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Step 1: Select Property
              </Typography>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={isLoadingWcOrders}
              >
                Refresh Orders
              </Button>
            </div>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.propertySelect}
              disabled={isLoadingWcOrders}
            >
              <InputLabel>Property</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => {
                  setSelectedRows([]);
                  setDryRunResult(null);
                  setSelectedAccountId(e.target.value);
                }}
                label="Property"
              >
                <MenuItem value="">
                  <em>Select a property</em>
                </MenuItem>
                {wcProperties.map((p) => {
                  const totals = propertyTotals[p.account_id];
                  return (
                    <MenuItem key={p.account_id} value={p.account_id}>
                      {p.staying_at}
                      {totals
                        ? ` — ${totals.count} unpaid (${currencyFormatter.format(
                            totals.transfer / 100
                          )})`
                        : " — no unpaid orders"}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {selectedProperty && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                Paid to <strong>{selectedProperty.stripe_account_name}</strong>{" "}
                ({selectedProperty.stripe_account_id})
              </Typography>
            )}

            {errorMessage && (
              <Alert severity="error" className={classes.skippedAlert}>
                {errorMessage}
              </Alert>
            )}

            {!isLoadingWcOrders && wcSkippedOrders.length > 0 && (
              <Alert
                severity="info"
                className={classes.skippedAlert}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setShowSkipped((v) => !v)}
                  >
                    {showSkipped ? "Hide" : "Show"}
                  </Button>
                }
              >
                {wcSkippedOrders.length} WooCommerce order
                {wcSkippedOrders.length !== 1 ? "s" : ""} since the cutoff
                can't be paid out here.
                <Collapse in={showSkipped}>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {wcSkippedOrders.map((s) => (
                      <li key={s.wc_order_id}>
                        <a
                          href={wcOrderAdminUrl(s.wc_order_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "inherit" }}
                        >
                          #{s.wc_order_id}
                        </a>{" "}
                        — {s.reason}
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Alert>
            )}
          </Paper>
        </Grid>

        {isLoadingWcOrders && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <div className={classes.loadingOverlay}>
                <CircularProgress />
                <Typography variant="body2" color="textSecondary">
                  Loading orders from WooCommerce…
                </Typography>
              </div>
            </Paper>
          </Grid>
        )}

        {/* Step 2: Orders Grid */}
        {selectedProperty && !isLoadingWcOrders && (
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Step 2: Select Orders
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Showing unpaid, Stripe-paid orders for{" "}
                <strong>{selectedProperty.staying_at}</strong>. Check the orders
                to include in this payout.
              </Typography>

              {dryRunResult && (
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                  Dry run only — the API has PAYOUT_DRY_RUN on, so no transfer
                  was made. It would have sent{" "}
                  <strong>
                    {currencyFormatter.format(dryRunResult.total_transfer)}
                  </strong>{" "}
                  to {dryRunResult.stripe_account_name} for{" "}
                  {dryRunResult.order_count} order
                  {dryRunResult.order_count !== 1 ? "s" : ""}.
                </Alert>
              )}

              <div
                className={
                  mode === "dark" ? "ag-theme-alpine-dark" : "ag-theme-alpine"
                }
                style={{ height: 500, width: "100%" }}
              >
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection="multiple"
                  suppressRowClickSelection
                  enableCellTextSelection
                  animateRows
                  overlayNoRowsTemplate={
                    errorMessage
                      ? `<span class='ag-overlay-no-rows-center'>${errorMessage}</span>`
                      : "<span class='ag-overlay-no-rows-center'>No unpaid orders for this property</span>"
                  }
                  onGridReady={onGridReady}
                  onSelectionChanged={onSelectionChanged}
                />
              </div>

              {/* Selection Summary */}
              {previewSummary && (
                <div className={classes.selectionSummary}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={8}>
                      <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 700, marginBottom: 4 }}
                      >
                        <strong>{selectedRows.length}</strong> order
                        {selectedRows.length !== 1 ? "s" : ""} selected
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.selectionSummaryText}
                      >
                        Orders Total:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalOrdersAmount
                          )}
                        </strong>
                        {"  ·  "}
                        Stripe Fees:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalStripeFees
                          )}
                        </strong>
                        {"  ·  "}
                        Our Commission ({previewSummary.commissionPct}%)*:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalCommission
                          )}
                        </strong>
                        {"  ·  "}
                        Transfer:{" "}
                        <strong style={{ color: "inherit" }}>
                          {currencyFormatter.format(
                            previewSummary.totalTransfer
                          )}
                        </strong>
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.selectionSummaryText}
                        style={{ opacity: 0.75 }}
                      >
                        * Commission applies to the order total after the
                        Stripe processing fee.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
                      <Button
                        variant="outlined"
                        style={{ borderColor: "white", color: "white" }}
                        onClick={() => setPreviewOpen(true)}
                      >
                        Preview &amp; Confirm Payout
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              )}

              {selectedRows.length === 0 && rowCount > 0 && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ marginTop: 16 }}
                >
                  Select at least one order to continue.
                </Typography>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>

      {previewSummary && (
        <WcPayoutPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          summary={previewSummary}
          onSuccess={handlePayoutSuccess}
        />
      )}
    </>
  );
};

export default WcPayoutNewPage;
